import { useQuery } from '@tanstack/react-query';
import {
  fetchExternalCurrentUser,
  type ExternalCurrentUser,
} from '../services/api/externalAuth';

/**
 * Phase 25 Plan 25-06 — minimal external-actor identity hook for the
 * comment surface mounted inside `TaskDetailSlideOver` (originally
 * `TaskDetailModal`; replaced in Phase 41 Plan 41-17).
 *
 * Only enabled when the audience is 'external'; returns null on 401
 * (e.g., the panel mounts inside a context that races the external
 * session resolution — the parent renders a "Loading comments…"
 * skeleton until the actor settles, see Phase 24 D-21/D-24).
 *
 * Why is `enabled` an explicit param rather than reading audience from
 * a context? Mirrors the audience-as-explicit-prop pattern locked by
 * Plan 25-04 (CommentComposer) and Plan 25-03 (MentionPicker) — the
 * caller decides whether the hook should query, never the hook itself
 * importing internal `useAuth` (Phase 24 D-21 landmine: external
 * surfaces must never reach into `services/authContext.tsx`).
 */
export function useExternalCurrentUser(enabled: boolean) {
  return useQuery<ExternalCurrentUser | null>({
    queryKey: ['external-me'],
    queryFn: async () => {
      try {
        return await fetchExternalCurrentUser();
      } catch {
        // 401 / network error → no active external session. Returning
        // null lets the consumer render a graceful skeleton instead of
        // surfacing an error to a UI that may legitimately be opening
        // before the session settles.
        return null;
      }
    },
    enabled,
    staleTime: 60_000,
  });
}
